import { useRef, useState } from 'react';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { Button } from '@/components/ui/button';
import { oversizeMessage } from '@/components/contracts/setup/inline/fileMeta';
import { FileText, ExternalLink, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SingleFileUploadProps {
    file: File | null;
    setFile: (file: File | null) => void;
    files?: never;
    setFiles?: never;
    multiple?: false;
    currentFileUrl?: string | null;
    accept?: string;
    maxSizeMB?: number;
    className?: string;
}

interface MultiFileUploadProps {
    file?: never;
    setFile?: never;
    files: File[];
    setFiles: (files: File[]) => void;
    multiple: true;
    currentFileUrl?: string | null;
    accept?: string;
    maxSizeMB?: number;
    className?: string;
}

type FileUploadProps = SingleFileUploadProps | MultiFileUploadProps;

export function FileUpload(props: FileUploadProps) {
    const {
        currentFileUrl,
        accept = ".pdf,.jpg,.jpeg,.png",
        maxSizeMB = 5,
        className,
        multiple,
    } = props;
    const fileInputRef = useRef<HTMLInputElement>(null);
    // One sentence per file this pick refused, cleared by the next pick.
    const [refused, setRefused] = useState<string[]>([]);

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    /**
     * THE LIMIT IS ENFORCED HERE BECAUSE HERE IS WHERE THE FILE FIRST EXISTS. The caption under
     * the drop zone has always named a limit (`… up to 5MB`), and every caller left the checking
     * to this component — so an oversized file used to be staged, carried through the form and
     * refused only by storage, at the end, with the admin's other work already typed. Refusing at
     * the pick is the only place the answer costs nothing, and it keeps one wording for the
     * refusal: `oversizeMessage` is the same sentence the setup canvas's `InlineUpload` prints.
     *
     * A refusal is per file, never per pick: in multiple mode the files within the limit are still
     * staged and only the oversized ones are named. The input is reset either way, so the admin
     * can re-choose the very file that was just refused once it has been shrunk.
     */
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        const verdicts = Array.from(selectedFiles).map((file) => ({
            file,
            tooBig: oversizeMessage(file, maxSizeMB),
        }));
        const within = verdicts.filter((v) => v.tooBig === null).map((v) => v.file);
        setRefused(verdicts.map((v) => v.tooBig).filter((m): m is string => m !== null));

        if (within.length > 0) {
            if (multiple) {
                props.setFiles([...props.files, ...within]);
            } else {
                props.setFile(within[0]);
            }
        }

        // Reset input so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClearFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiple) {
            props.setFiles([]);
        } else {
            props.setFile(null);
        }
        setRefused([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (e: React.MouseEvent, index: number) => {
        e.stopPropagation();
        if (multiple) {
            props.setFiles(props.files.filter((_, i) => i !== index));
        }
    };

    const hasFiles = multiple ? props.files.length > 0 : !!props.file;

    return (
        <div className={cn("space-y-2", className)}>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileChange}
                className="hidden"
            />

            <div
                onClick={handleFileClick}
                className="relative group cursor-pointer border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 hover:bg-muted/30 transition-all"
            >
                {hasFiles ? (
                    multiple ? (
                        <div className="space-y-2">
                            {props.files.map((f, i) => (
                                <div key={`${f.name}-${i}`} className="flex items-center justify-center gap-3">
                                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                                    <div className="text-left min-w-0">
                                        <p className="text-sm font-medium line-clamp-1 max-w-[200px]">{f.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {(f.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 flex-shrink-0"
                                        onClick={(e) => handleRemoveFile(e, i)}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                            <p className="text-xs text-muted-foreground mt-2">Click to add more</p>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center gap-3">
                            <FileText className="h-8 w-8 text-primary" />
                            <div className="text-left">
                                <p className="text-sm font-medium line-clamp-1 max-w-[200px]">{props.file!.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {(props.file!.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 ml-2"
                                onClick={handleClearFile}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    )
                ) : (
                    <>
                        <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                            <Upload className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <p className="text-sm font-medium">Upload document{multiple ? 's' : ''}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')} up to {maxSizeMB}MB
                        </p>
                    </>
                )}
            </div>

            {/* `role="alert"` rather than a mounted live region: the line exists only while a
                refusal stands, and an alert is announced on insertion. */}
            {refused.length > 0 && (
                <div className="space-y-1" role="alert">
                    {refused.map((message, i) => (
                        <p key={`${message}-${i}`} className="text-xs text-destructive">
                            {message}
                        </p>
                    ))}
                </div>
            )}

            {currentFileUrl && !hasFiles && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => openSignedUrl(currentFileUrl!)}
                >
                    <FileText className="h-4 w-4 mr-2" />
                    View current document
                    <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
            )}
        </div>
    );
}

